<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  type JobConfigurationDto,
  EntityWithCommentType,
  ReferencingObjectDto,
} from '@/api/generator';
import { useFormMode } from '@/components/forms/useFormMode';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { type TabDefinition } from '@/components/Tabs/types.ts';
import { routenames } from '@/router/routenames';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { useRouter } from 'vue-router';
import { useDeleteJobConfigurationWithConfirmation } from '../utils';
import JobConfigurationAdministrativeTab from './JobConfigurationAdministrativeTab.vue';
import JobConfigurationTab from './JobConfigurationTab.vue';
import Comments from '@/components/comments/Comments.vue';
import { EntityInfo } from '@/components/confirmationModals/types';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { useAmountComments } from '@/components/comments/useAmountComments';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { useFormTab } from '@/components/forms/useFormTab';

const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const router = useRouter();

const { t } = useTranslation();

const mode = useFormMode();

const values = useFormValues<JobConfigurationDto>();

const { commentsChanged, commentsTabTitle } = useAmountComments(values);

const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        indicatorState: getIndicatorState([
          'name',
          'description',
          'attachments',
          'protocols',
          'assignedUsers',
          'assignedRoles',
          'reviewUsers',
          'reviewRoles',
          'schedule',
        ]),
      },
      configuration: {
        title: t('Configuration'),
        hideContentSheet: true,
        indicatorState: getIndicatorState(['tasks']),
      },
      comments: {
        ...defaultPageTabs.comments,
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
      },
    }) satisfies TabDefinition,
);

const { action: deleteAction } = useDeleteJobConfigurationWithConfirmation();

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);

  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.jobConfigurations.edit,
        params: { id: values.value.id },
      },
    },
    {
      ...defaultPageActions.copy,
      to: {
        name: routenames.jobConfigurations.copy,
        params: { copyId: values.value.id },
      },
    },
    {
      ...defaultPageActions.delete,
      async callback() {
        const defaultConfirmationOptions = getDefaultDeleteConfirmationOptions(
          {
            ...(values.value as EntityInfo),
            typeName: 'JobConfiguration_one',
          },
          t,
        );

        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: {
              ...defaultConfirmationOptions,
              size: 'large',
              contentData: {
                ...defaultConfirmationOptions.contentData,
                hint: t('JobConfigurationDeletionEffectsMsg', {
                  hint: defaultConfirmationOptions.contentData.hint,
                }),
              },
            },
          });
          if (result?.success) {
            router.push({ name: routenames.jobConfigurations.overview });
          }
        }
      },
    },
  ];
});
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <InlineNotification
        v-if="mode === 'edit'"
        class="mb-4"
        variant="flat"
        :value="{
          type: 'info',
          message: $t('JobConfigurationReferencesBannerMsg'),
        }"
      >
        <template #append>
          <span
            class="ml-2 linkButton"
            @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
            >{{ $t('Info') }}</span
          >
        </template>
      </InlineNotification>

      <JobConfigurationAdministrativeTab />
    </template>

    <template #configuration>
      <InlineNotification
        v-if="mode === 'edit'"
        class="mb-4"
        variant="flat"
        :value="{
          type: 'info',
          message: $t('JobConfigurationReferencesBannerMsg'),
        }"
      >
        <template #append>
          <span
            class="ml-2 linkButton"
            @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
            >{{ $t('Info') }}</span
          >
        </template>
      </InlineNotification>

      <JobConfigurationTab />
    </template>

    <template #comments>
      <Comments
        v-if="values.id"
        :id="values.id"
        :entityWithCommentType="EntityWithCommentType.JobConfiguration"
        @change="commentsChanged"
      />
    </template>
  </BasePageNavigation>

  <EntityReferencesModal
    :title="$t('ReferenceingEntities')"
    :references="entityReferences"
    :hideRefTable="true"
    size="large"
    @dialogOpen="
      (isOpen: boolean) => {
        if (!isOpen) entityReferences = null;
      }
    "
  >
    <template #description>
      {{ $t('JobConfigurationReferencesDescriptionMsg') }}
    </template>
  </EntityReferencesModal>
</template>
